import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  ChevronRight, Phone, ArrowRight, RotateCcw, CheckCircle2, AlertTriangle, Droplet,
} from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";

const TOTAL_SEC = 15 * 60;
const CIRC = 2 * Math.PI * 98;

const StepDots = ({ current }) => (
  <div className="flex gap-2 mb-10">
    {[1, 2, 3, 4].map((n) => (
      <div
        key={n}
        className={`h-[3px] flex-1 transition-colors duration-500 ${
          n <= current ? "bg-gold" : "bg-line"
        }`}
      />
    ))}
  </div>
);

const DigitInput = ({ id, values, setValues, redFrom = 2 }) => {
  const refs = useRef([]);
  const onChange = (idx, v) => {
    const digit = v.replace(/[^0-9]/g, "").slice(0, 1);
    const next = [...values];
    next[idx] = digit;
    setValues(next);
    if (digit && idx < 3) refs.current[idx + 1]?.focus();
  };
  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !values[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };
  return (
    <div className="flex gap-2 my-6" data-testid={id}>
      {[0, 1, 2, 3].map((i) => {
        const isRed = i >= redFrom;
        return (
          <input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={values[i] || ""}
            onChange={(e) => onChange(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            aria-label={`${isRed ? "Red" : "Black"} digit ${i + 1}`}
            data-testid={`${id}-digit-${i}`}
            className={`w-14 h-20 md:w-16 md:h-24 text-center font-body font-medium text-3xl md:text-4xl bg-card border transition-all outline-none focus:border-gold focus:ring-1 focus:ring-gold/40 ${
              isRed
                ? "text-gold border-gold/40 bg-gold/[0.04]"
                : "text-ink border-line"
            }`}
          />
        );
      })}
    </div>
  );
};

const Gauge = ({ remaining }) => {
  const pct = remaining / TOTAL_SEC;
  const offset = CIRC * (1 - pct);
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  const label =
    remaining <= 0 ? "DONE" : `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return (
    <div className="relative w-[240px] h-[240px] mx-auto" data-testid="leak-gauge">
      <svg width="240" height="240" viewBox="0 0 220 220" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="110" cy="110" r="98" fill="none" stroke="#1c1c1c" strokeWidth="6" />
        <circle
          cx="110"
          cy="110"
          r="98"
          fill="none"
          stroke="#E5B842"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1s linear" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-body font-medium text-4xl md:text-5xl text-ink tabular-nums" data-testid="leak-time">
          {label}
        </span>
        <span className="mt-2 font-body text-[10px] tracking-[0.3em] uppercase text-muted">
          Remaining
        </span>
      </div>
    </div>
  );
};

const LeakTest = () => {
  const [step, setStep] = useState(1);
  const [reading1, setReading1] = useState(["", "", "", ""]);
  const [reading2, setReading2] = useState(["", "", "", ""]);
  const [remaining, setRemaining] = useState(TOTAL_SEC);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);
  const toolRef = useRef(null);

  const goStep = useCallback((n) => {
    setStep(n);
    setTimeout(() => toolRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, []);

  useEffect(() => {
    return () => timerRef.current && clearInterval(timerRef.current);
  }, []);

  const readValue = (arr) => {
    if (arr.some((v) => v === "")) return null;
    return parseInt(arr.join(""), 10);
  };

  const startTimer = () => {
    if (readValue(reading1) === null) {
      alert("Enter all four digits from your meter first.");
      return;
    }
    setRunning(true);
    setDone(false);
    setRemaining(TOTAL_SEC);
    goStep(3);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          setDone(true);
          setRunning(false);
          if ("vibrate" in navigator) navigator.vibrate([200, 100, 200]);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };

  const skipTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRemaining(0);
    setDone(true);
    setRunning(false);
  };

  const calculate = () => {
    const r1 = readValue(reading1);
    const r2 = readValue(reading2);
    if (r1 === null || r2 === null) {
      alert("Enter all four digits for both readings.");
      return;
    }
    let diff = r2 - r1;
    if (diff < 0) diff += 10000; // meter rollover
    const perHour = diff * 4;
    const perDay = perHour * 24;
    setResult({ diff, perHour, perDay });
    setTimeout(() => {
      document.getElementById("leak-result")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  };

  const restart = () => {
    setStep(1);
    setReading1(["", "", "", ""]);
    setReading2(["", "", "", ""]);
    setRemaining(TOTAL_SEC);
    setRunning(false);
    setDone(false);
    setResult(null);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  return (
    <div data-testid="leak-test-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gold-glow opacity-50 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full border border-gold/10 pointer-events-none" />
        <div className="absolute top-16 right-16 w-[220px] h-[220px] rounded-full border border-gold/20 pointer-events-none" />
        <div className="relative mx-auto max-w-page px-6 lg:px-14 pt-20 pb-24">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <Link to="/#tools" className="text-muted hover:text-gold">Tools</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">Leak Detection</span>
          </nav>
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow mb-6"
            >
              <span>Free Tool · Leak Detection</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black text-[clamp(3rem,9vw,7.5rem)] leading-[0.9] text-ink"
            >
              Find a hidden leak
              <br />
              <span className="text-gold">in 15 minutes.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-8 text-muted text-lg md:text-xl max-w-xl leading-relaxed"
            >
              A dripping tap is easy to spot. The leaks that cost you real money
              are the ones you can&apos;t see. This test uses your own water
              meter to find them — no tools, no cost, no plumber required.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 border-t border-line pt-8"
            >
              <div>
                <div className="font-body font-medium text-2xl text-gold tabular-nums">10,000L+</div>
                <div className="mt-1 text-muted text-xs tracking-widerx uppercase leading-relaxed">
                  A single leaking tap can waste per year
                </div>
              </div>
              <div>
                <div className="font-body font-medium text-2xl text-gold tabular-nums">9,000L</div>
                <div className="mt-1 text-muted text-xs tracking-widerx uppercase leading-relaxed">
                  A running toilet can waste per week
                </div>
              </div>
              <div>
                <div className="font-body font-medium text-2xl text-gold tabular-nums">15 min</div>
                <div className="mt-1 text-muted text-xs tracking-widerx uppercase leading-relaxed">
                  To run this test
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before you start */}
      <section className="py-16 bg-elevated border-y border-line">
        <div className="mx-auto max-w-page px-6 lg:px-14 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="eyebrow flex-shrink-0">Before you start</div>
          <p className="text-muted text-lg leading-relaxed max-w-3xl">
            Leaks grow over time and turn into much bigger repair bills — water
            damage, mould, rotted framing. If your water use has increased and
            nothing about your household has changed, run this test. It takes
            one condition to be accurate:{" "}
            <strong className="text-ink">
              no water can be used anywhere on the property for the full 15 minutes.
            </strong>
          </p>
        </div>
      </section>

      {/* The tool */}
      <section ref={toolRef} id="tool" className="py-24 lg:py-32">
        <div className="mx-auto max-w-page px-6 lg:px-14">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-5">The Test</div>
            <h2 className="font-display text-5xl md:text-7xl text-ink mb-6">Meter Leak Test</h2>
            <p className="text-muted text-lg">
              Four steps. Your water meter measures every litre that enters the property
              — if it moves while nothing is running, something is leaking.
            </p>
          </div>

          <StepDots current={step} />

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="bg-card border border-line p-8 md:p-12"
              data-testid={`leak-step-${step}`}
            >
              {step === 1 && (
                <div>
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Step 1 of 4</div>
                  <h3 className="font-display text-3xl md:text-4xl text-ink mb-5">Turn everything off</h3>
                  <p className="text-muted mb-6 max-w-xl">
                    Make sure no water is being used anywhere on the property. Check:
                  </p>
                  <ul className="space-y-3 max-w-xl">
                    {[
                      "All taps — inside and outside",
                      "Dishwasher and washing machine not mid-cycle",
                      "Evaporative cooler off (they draw water)",
                      "Irrigation and garden watering systems off",
                      "Nobody flushes a toilet during the test",
                    ].map((li, i) => (
                      <li key={i} className="flex items-start gap-3 text-ink">
                        <span className="mt-2 w-4 h-px bg-gold flex-shrink-0" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <button
                      onClick={() => goStep(2)}
                      className="btn-gold"
                      data-testid="leak-step1-next"
                    >
                      Everything's off <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Step 2 of 4</div>
                  <h3 className="font-display text-3xl md:text-4xl text-ink mb-5">Read your meter</h3>
                  <p className="text-muted max-w-xl">
                    Your water meter is usually in the front garden near the street.
                    Lift the cap and enter the last four digits — the{" "}
                    <b className="text-gold">red digits</b> measure litres and are the
                    ones that move first when water flows.
                  </p>
                  <DigitInput id="reading1" values={reading1} setValues={setReading1} />
                  <div className="text-muted text-xs tracking-widerx uppercase">
                    Black digits = kilolitres · <b className="text-gold">Red digits = litres</b>
                  </div>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <button onClick={() => goStep(1)} className="btn-ghost" data-testid="leak-step2-back">
                      ← Back
                    </button>
                    <button onClick={startTimer} className="btn-gold" data-testid="leak-step2-start">
                      Start 15-min test <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="text-center">
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Step 3 of 4</div>
                  <h3 className="font-display text-3xl md:text-4xl text-ink mb-8">Wait — and use no water</h3>
                  <Gauge remaining={remaining} />
                  <p className="mt-8 text-muted text-sm max-w-md mx-auto">
                    Leave this page open. If any water gets used during the test,
                    the result won't be reliable — restart the test.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4 justify-center">
                    <button
                      onClick={skipTimer}
                      className="btn-ghost text-[10px]"
                      title="For demo/testing only"
                      data-testid="leak-step3-skip"
                    >
                      Skip wait (demo)
                    </button>
                    <button
                      onClick={() => goStep(4)}
                      className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={!done}
                      data-testid="leak-step3-next"
                    >
                      Enter second reading <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div>
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Step 4 of 4</div>
                  <h3 className="font-display text-3xl md:text-4xl text-ink mb-5">Read your meter again</h3>
                  <p className="text-muted max-w-xl">
                    Go back to the meter and enter the same four digits now.
                  </p>
                  <DigitInput id="reading2" values={reading2} setValues={setReading2} />
                  <div className="text-muted text-xs tracking-widerx uppercase">
                    Same four digits, same order
                  </div>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <button onClick={() => goStep(3)} className="btn-ghost" data-testid="leak-step4-back">
                      ← Back
                    </button>
                    <button onClick={calculate} className="btn-gold" data-testid="leak-step4-submit">
                      Get my result <ArrowRight size={16} />
                    </button>
                  </div>

                  {result && (
                    <motion.div
                      id="leak-result"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className={`mt-10 p-8 md:p-10 border-l-4 ${
                        result.diff === 0 ? "border-emerald-500 bg-emerald-500/[0.04]" : "border-emergency bg-emergency/[0.04]"
                      }`}
                      data-testid="leak-result"
                    >
                      {result.diff === 0 ? (
                        <>
                          <div className="flex items-center gap-3 mb-4">
                            <CheckCircle2 size={26} className="text-emerald-400" />
                            <span className="font-display text-2xl md:text-3xl uppercase text-emerald-400">
                              No leak detected
                            </span>
                          </div>
                          <div className="font-body font-medium text-3xl text-ink mb-1 tabular-nums">
                            0 L
                          </div>
                          <div className="text-muted text-xs tracking-widerx uppercase mb-6">Used in 15 min</div>
                          <p className="text-muted max-w-xl leading-relaxed">
                            Your meter didn't move — no water flowed onto the property during
                            the test. If your bills are still climbing, the increase is coming
                            from usage, not a leak. Worth checking appliance settings,
                            irrigation schedules, or household habits.
                          </p>
                          <div className="mt-6">
                            <button onClick={restart} className="btn-ghost" data-testid="leak-restart">
                              <RotateCcw size={14} /> Run test again
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle size={26} className="text-emergency" />
                            <span className="font-display text-2xl md:text-3xl uppercase text-emergency">
                              Leak detected
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6 mb-8">
                            <div>
                              <div className="font-body font-medium text-3xl text-ink tabular-nums">{result.diff} L</div>
                              <div className="text-muted text-xs tracking-widerx uppercase mt-1">Lost in 15 min</div>
                            </div>
                            <div>
                              <div className="font-body font-medium text-3xl text-ink tabular-nums">~{result.perHour} L</div>
                              <div className="text-muted text-xs tracking-widerx uppercase mt-1">Per hour</div>
                            </div>
                            <div>
                              <div className="font-body font-medium text-3xl text-ink tabular-nums">~{result.perDay.toLocaleString()} L</div>
                              <div className="text-muted text-xs tracking-widerx uppercase mt-1">Per day</div>
                            </div>
                          </div>
                          <p className="text-muted leading-relaxed max-w-xl">
                            Your meter moved while no water was in use — water is escaping
                            somewhere on the property. Check the common culprits below
                            (toilets first — they're the usual offender). If nothing visible
                            is leaking, the leak is concealed and needs professional leak
                            detection before it causes damage.
                          </p>
                          <div className="mt-6 flex flex-wrap gap-3">
                            <a href={PHONE_HREF} className="btn-gold" data-testid="leak-book-cta">
                              <Phone size={16} /> Book Styne Group
                            </a>
                            <button onClick={restart} className="btn-ghost" data-testid="leak-restart">
                              <RotateCcw size={14} /> Run test again
                            </button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Where leaks hide */}
      <section className="py-24 lg:py-32 bg-elevated">
        <div className="mx-auto max-w-page px-6 lg:px-14">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-5">If the meter moved</div>
            <h2 className="font-display text-5xl md:text-7xl text-ink">Where leaks hide.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-line">
            {[
              { tag: "Toilets", title: "Silent cistern leaks", desc: "Water trickling from cistern to bowl with no sound. The most common hidden leak — and one of the most wasteful." },
              { tag: "Taps", title: "Drips & worn washers", desc: "Check laundry troughs, outside garden taps and hose fittings — not just kitchen and bathroom." },
              { tag: "Hot water", title: "Relief valve discharge", desc: "A hot water unit constantly dripping from its valve is losing water and energy around the clock." },
              { tag: "Irrigation", title: "Underground solenoids", desc: "Unexplained wet patches or unusually green strips of lawn can mean a leaking irrigation line." },
              { tag: "Evap coolers", title: "Roof unit float valves", desc: "Evaporative coolers draw mains water — a stuck float valve keeps drawing even when it's off." },
              { tag: "Concealed pipes", title: "In-wall & underground", desc: "Damp patches, mould, spongy floors or a musty smell near walls can mean a concealed pipe leak. These need acoustic leak detection." },
            ].map((it, i) => (
              <div
                key={i}
                data-testid={`leak-hidden-${i}`}
                className="bg-elevated hover:bg-card transition-colors p-8"
              >
                <div className="text-gold text-xs tracking-widerx uppercase mb-4">{it.tag}</div>
                <h4 className="font-display text-xl text-ink mb-3">{it.title}</h4>
                <p className="text-muted text-sm leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-16 bg-gold text-bg">
        <div className="mx-auto max-w-page px-6 lg:px-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h2 className="font-display font-black text-3xl md:text-5xl leading-tight max-w-2xl">
            Meter moved? We'll find it and fix it.
          </h2>
          <a
            href={PHONE_HREF}
            className="inline-flex items-center gap-2 bg-bg text-gold border border-bg px-6 py-4 font-display tracking-widerx uppercase text-sm hover:bg-transparent hover:text-bg transition-colors"
            data-testid="leak-cta-call"
          >
            <Phone size={16} /> Call {PHONE}
          </a>
        </div>
      </section>
    </div>
  );
};

export default LeakTest;
